import React, { useState, useEffect } from "react";
import List from '@mui/material/List';
import { ListItem, FormControlLabel, FormGroup, Checkbox, Typography, RadioGroup, Radio } from "@mui/material";
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import "./FiltersContainer.css";
import filters from "../Filters.json";
import TitleFilter from "../customFilters/TitleFilter";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch } from 'react-redux';
import { clearFilters, setFilterFeatureSelection, setDataset, setPostFireCategories, setPostFireType, setFuelsCategories, setFuelsType } from '../../../redux/slices/filterSlice';
import FieldNames from '../../../defaults/FieldNames.json';
import ColorConstants from "../../../defaults/ColorConstants";
import { updateFilterExpression } from "../../../utils/ExpressionHelper";

const useStyles = makeStyles(() => ({
  filterCollapser: {
    display:'flex', 
    flex: '0 0 56px', 
    width: '100%', 
    alignItems: 'center', 
    textAlign: 'center',
    boxSizing: 'border-box', 
    cursor: 'pointer', 
    transition: 'color 275ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
    backgroundColor: ColorConstants.WhiteAlien,
    color: ColorConstants.BlueAlien, 
    justifyContent:'center', 
    '&:hover': {
      backgroundColor: ColorConstants.BlueAlien,
      color: ColorConstants.WhiteAlien
    }
  },
  filterItemSelection: {
    display:'flex', 
    justifyContent:'flex-start', 
    padding: '10px 4px 10px 10px',
    backgroundColor: ColorConstants.WhiteAlien, 
    color: ColorConstants.BlueAlien, 
    width: '100%',
    flexDirection: 'row',
    borderBottom: '1px solid', 
    borderBottomColor: ColorConstants.BlueAlien,
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
  filterCollapserButton: {
    display:'flex', 
    flex: '0 0 56px', 
    width: '100%', 
    alignItems: 'center', 
    textAlign: 'center',
    boxSizing: 'border-box', 
    cursor: 'pointer', 
    transition: 'color 275ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
    backgroundColor: ColorConstants.WhiteAlien,
    color: ColorConstants.BlueAlien, 
    justifyContent:'center', 
    borderBottom: '1px solid', 
    borderBottomColor: ColorConstants.BlueAlien,
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
}));

const theme = createTheme({
  overrides: {
    MuiListItemButton: {
      root: {
        margin: 0,
        padding: 10,
        '&:hover $filterCollapser': {
          color: ColorConstants.BlueAlien,
        },
      },
    },
  },
});

export default function FiltersContainer ({userInfo, congressDistValues, isCreatingActualActivity,
  isCreatingActualTreatment, isCreatingPlannedTreatment, isCreatingPlannedActivity, wbsIDValues,
  rootServiceUrl, config }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [filterItems, setFilterItems] = useState([]);
  const [typeSelection, setTypeSelection] = useState("");
  const [isRTRLSelection, setIsRTRLSelection] = useState("");
  const [isCarryoverSelection, setIsCarryoverSelection] = useState("");
  const [categorySelection, setCategorySelection] = useState("");
  const [prioritiesSelection, setPrioritiesSelection] = useState("");
  const [fundingSourceSelection, setFundingSourceSelection] = useState("");
  const [noneApprovalsSelection, setNoneApprovalsSelection] = useState("");
  const [allApprovalsSelection, setAllApprovalsSelection] = useState("");
  const [initiationFiscalQuarterSelection, setInitiationFiscalQuarterSelection] = useState("");
  const [completionFiscalQuarterSelection, setCompletionFiscalQuarterSelection] = useState("");
  const [statusSelection, setStatusSelection] = useState("");
  const basicFilterFeatureSelection = useSelector((state) => { return state.filter.filterFeatureSelection });
  const treatmentCount = useSelector((state) => state.globalContext.treatmentCount);
  const treatmentCountQueryError = useSelector((state) => state.globalContext.treatmentCountQueryError);
  const activityCount = useSelector((state) => state.globalContext.activityCount);
  const actualTreatmentCount = useSelector((state) => state.globalContext.actualTreatmentCount);
  const actualActivityCount = useSelector((state) => state.globalContext.actualActivityCount);
  const activityCountQueryError = useSelector((state) => state.globalContext.activityCountQueryError);
  const actualTreatmentCountQueryError = useSelector((state) => state.globalContext.actualTreatmentCountQueryError);
  const actualActivityCountQueryError = useSelector((state) => state.globalContext.actualActivityCountQueryError);
  const isTreatmentCountQuerying = useSelector((state) => state.globalContext.isTreatmentCountQuerying);
  const isActivityCountQuerying = useSelector((state) => state.globalContext.isActivityCountQuerying);
  const isActualTreatmentCountQuerying = useSelector((state) => state.globalContext.isActualTreatmentCountQuerying);
  const isActualActivityCountQuerying = useSelector((state) => state.globalContext.isActualActivityCountQuerying);
  
  const isUserPostfire = userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
  const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes;
  const storedDataset = useSelector((state) => state.filter.dataset);
  const [datasetSelection,setDatasetSelection] = useState(((isUserPostfire && isUserFuels) ? storedDataset :
                                                           ((isUserPostfire && !isUserFuels) ? "POSTFIRE" : "FUELS")));

  useEffect(() => {
    if(storedDataset !== datasetSelection)
    {
      if(!storedDataset){
        const tempDataset = ((isUserPostfire && isUserFuels) ? "FUELS" : ((isUserPostfire && !isUserFuels) ? "POSTFIRE" : "FUELS"));
        setDatasetSelection(tempDataset);
        dispatch(setDataset(tempDataset))
      } else {
        setDatasetSelection(storedDataset);
      }
      
    }
  }, [storedDataset, isUserPostfire, isUserFuels])

  useEffect(() => {
    setTypeSelection(typeSelection);
  }, [typeSelection, filterItems]);

  useEffect(() => {
    setIsRTRLSelection(isRTRLSelection);
  }, [isRTRLSelection, filterItems]);

  useEffect(() => {
    setIsCarryoverSelection(isCarryoverSelection);
  }, [isCarryoverSelection, filterItems]);

  useEffect(() => {
    setStatusSelection(statusSelection);
  }, [statusSelection, filterItems]);

  useEffect(() => {
    setCategorySelection(categorySelection);
  }, [categorySelection, filterItems]);

  useEffect(() => {
    setCompletionFiscalQuarterSelection(completionFiscalQuarterSelection);
  }, [completionFiscalQuarterSelection, filterItems]);

  useEffect(() => {
    setInitiationFiscalQuarterSelection(initiationFiscalQuarterSelection);
  }, [initiationFiscalQuarterSelection, filterItems]);

  useEffect(() => {
    setPrioritiesSelection(prioritiesSelection);
  }, [prioritiesSelection, filterItems]);

  useEffect(() => {
    setFundingSourceSelection(fundingSourceSelection);
  }, [fundingSourceSelection, filterItems]);

  useEffect(() => {
    setAllApprovalsSelection(allApprovalsSelection);
  }, [allApprovalsSelection, filterItems]);

  useEffect(() => {
    setNoneApprovalsSelection(noneApprovalsSelection);
  }, [noneApprovalsSelection, filterItems]);

  useEffect(() => 
    {
      var items = [];
      filters?.featureFilters.forEach(element => {
        if(element.name === "Department" || element.name === "Agency" || element.name === "Region" || element.name === "Unit") {
        } else {
          items.push(element);
        }
      });
      
      setFilterItems(items);
      const expressionString = updateFilterExpression();
    }, []);

  const filterHeader = () => 
  {
    const trtCountVal = (treatmentCountQueryError) ? "-" : treatmentCount;
    const actCountVal = (activityCountQueryError) ? "-" : activityCount;
    const treatmentIndicator = (treatmentCount === 1) ? "Estimated Treatment" : "Estimated Treatments";
    const treatmentLabel = trtCountVal + " " + treatmentIndicator;
    const activityIndicator = (activityCount === 1) ? "Estimated Activity" : "Estimated Activities";
    const activityLabel = actCountVal + " " + activityIndicator;
    const label = treatmentLabel + ", " + activityLabel + ", ";
    return (isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying) ? (<CircularProgress size={10} />) : label;
  }

  const filterHeaderActuals = () => 
  {
    const trtCountVal = (actualTreatmentCountQueryError) ? "-" : actualTreatmentCount;
    const actCountVal = (actualActivityCountQueryError) ? "-" : actualActivityCount;
    const actualsTreatmentIndicator = (actualTreatmentCount === 1) ? "Actual Treatment" : "Actual Treatments";
    const actualsTreatmentLabel = trtCountVal + " " + actualsTreatmentIndicator;
    const actualsActivityIndicator = (actualActivityCount === 1) ? "Actual Activity" : "Actual Activities";
    const actualsActivityLabel = actCountVal + " " + actualsActivityIndicator;
    const label = actualsTreatmentLabel + ", " + actualsActivityLabel;
    return label;
  }

  const basicFilterFeatureChanged = (e, filterType) => {
    if(e.target.checked){
      if(basicFilterFeatureSelection && !basicFilterFeatureSelection.includes(filterType)){
        let clone = [...basicFilterFeatureSelection];
        clone.push(filterType);
        dispatch(setFilterFeatureSelection(clone));
      }
    } else {
      if(basicFilterFeatureSelection && basicFilterFeatureSelection.includes(filterType)){
        const clone = [...basicFilterFeatureSelection];
        clone.splice(clone.indexOf(filterType), 1);
        dispatch(setFilterFeatureSelection(clone));
      }
    }
  }

  const clearFiltersHandler = () => {
    dispatch(clearFilters());
  }

  const handleRadioFilterChange = (event) => {
    dispatch(setPostFireCategories([]))
    dispatch(setPostFireType([]))
    dispatch(setFuelsCategories([]))
    dispatch(setFuelsType([]))
    setDatasetSelection(event.target.value);
    dispatch(setDataset(event.target.value));
  }

    return (
      <div className="filter-details">
        <ThemeProvider theme={theme}>
          <div className="filterList-panel">
            <List 
              sx={{ height: '100%', overflowY: 'hidden', paddingTop: 0, paddingBottom: 0 }}
              component="nav" className="filter-list"
              aria-labelledby="nested-list" 
            >
              <ListItemButton onClick={clearFiltersHandler} className={classes.filterCollapserButton} >
                <ListItemText primary="Reset Filters" />
              </ListItemButton>
              <ListItem className={"filter-header"} style={{display:'flex', justifyContent:'center', margin:'0px', zIndex: '5', color: ColorConstants.BlueAlien, backgroundColor: ColorConstants.LightGrayAlien,
                fontWeight: 300, fontStyle: 'italic', textAlign: 'center', boxSizing: 'border-box', borderBottom: '1px solid', borderBottomColor: ColorConstants.BlueAlien, height: 'auto', minHeight: '55px'}}>
                {(isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying) ? 
                  (<CircularProgress size={10} />) : (<ListItemText style={{ wordBreak: 'break-word'}} primary={filterHeader()} 
                  secondary={
                    <React.Fragment>
                      <Typography
                        sx={{ display: 'inline' }}
                        component="span"
                        variant="body2"
                        color={ColorConstants.BlueAlien}
                        fontStyle={'italic'}
                        fontWeight={300}
                        fontSize={16}
                      >
                        {(isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying) ? '' : filterHeaderActuals()}
                      </Typography>
                    </React.Fragment>
                  } 
                />)}
              </ListItem>
              {
                isUserPostfire && isUserFuels &&
                <ListItem>
                <RadioGroup
                      row
                      aria-labelledby="filter-controlled-radio-buttons-group"
                      name="filter-controlled-radio-buttons-group"
                      value={datasetSelection}
                      onChange={handleRadioFilterChange}
                      style={{display: 'flex', justifyContent: 'center', minWidth: 500}}
                    >
                        <FormControlLabel value="FUELS" control={<Radio />} label="Fuels" />
                        <FormControlLabel value="POSTFIRE" control={<Radio />} label="PostFire" />
                        <FormControlLabel value="BOTH" control={<Radio />} label="Both" />
                    </RadioGroup>
                </ListItem> 
                
              }
              <ListItem style={{ margin: 0, padding: 0 }}>
                <FormGroup className={classes.filterItemSelection} >
                  <FormControlLabel style={{ margin: '2px 0 2px 0' }} control={
                    <Checkbox style={{ padding: '0', margin: '0 5px 0 0px', borderColor: ColorConstants.BlueAlien }} id="treatments" checked={basicFilterFeatureSelection? basicFilterFeatureSelection.includes("Estimated Treatment") : false} onClick={(e) => basicFilterFeatureChanged(e, FieldNames.classTypes.treatment)} />
                  } label="Estimated Treatments" />
                  <FormControlLabel style={{ margin: '2px 0 2px 0px' }} control={
                    <Checkbox style={{ padding: '0', margin: '0 5px 0 0px', borderColor: ColorConstants.BlueAlien }} id="activities" checked={basicFilterFeatureSelection? basicFilterFeatureSelection.includes("Estimated Activity") : false} onClick={(e) => basicFilterFeatureChanged(e, FieldNames.classTypes.activity)} />
                  } label="Estimated Activities" />
                </FormGroup>
                <FormGroup className={classes.filterItemSelection} >
                  <FormControlLabel style={{ margin: '2px 0 2px 0' }} control={
                    <Checkbox style={{ padding: '0', margin: '0 5px 0 0px', borderColor: ColorConstants.BlueAlien }} id="actualTreatments" checked={basicFilterFeatureSelection? basicFilterFeatureSelection.includes("Actual Treatment") : false} onClick={(e) => basicFilterFeatureChanged(e, FieldNames.classTypes.actualTreatment)} />
                  } label="Actual Treatments " />
                  <FormControlLabel style={{ margin: '2px 0 2px 0px' }} control={
                    <Checkbox style={{ padding: '0', margin: '0 5px 0 0px', borderColor: ColorConstants.BlueAlien }} id="actualActivities" checked={basicFilterFeatureSelection? basicFilterFeatureSelection.includes("Actual Activity") : false} onClick={(e) => basicFilterFeatureChanged(e, FieldNames.classTypes.actualActivity)} />
                  } label="Actual Activities " />
                </FormGroup>
              </ListItem>
              {filterItems.map((eaFilter, i) => (
                <TitleFilter 
                  userInfo={userInfo}
                  filter={eaFilter}  
                  typeSelection={typeSelection} 
                  isRTRLSelection={isRTRLSelection}
                  isCarryoverSelection={isCarryoverSelection}
                  categorySelection={categorySelection}
                  fundingSourceSelection={fundingSourceSelection}
                  congressDistValues={congressDistValues}
                  prioritiesSelection={prioritiesSelection}
                  initiationFiscalQuarterSelection={initiationFiscalQuarterSelection}
                  completionFiscalQuarterSelection={completionFiscalQuarterSelection}
                  allApprovalsSelection={allApprovalsSelection}
                  noneApprovalsSelection={noneApprovalsSelection}
                  statusSelection={statusSelection}
                  isCreatingActualActivity={isCreatingActualActivity} 
                  isCreatingActualTreatment={isCreatingActualTreatment} 
                  isCreatingPlannedTreatment={isCreatingPlannedTreatment} 
                  isCreatingPlannedActivity={isCreatingPlannedActivity} 
                  wbsIDValues={wbsIDValues}
                  key={i}
                  rootServiceUrl={rootServiceUrl}
                  config={config}
                  datasetSelection={datasetSelection}
                />
              ))}
              <ListItemButton onClick={clearFiltersHandler} className={classes.filterCollapser} >
                <ListItemText primary="Reset Filters" />
              </ListItemButton>
            </List>
          </div>
        </ThemeProvider>
      </div>
    )
}